import { useEffect, useRef, useState } from 'react';

import { previewKindFor } from './useBlobPreview';

/**
 * Object URLs for the pictures a document places in its own text, keyed by attachment id.
 *
 * **Why this exists rather than `useBlobPreview` in a loop.** That hook resolves ONE file and
 * is a hook, so a document with three pictures cannot call it three times without the count
 * becoming part of the render. This resolves a SET, and the set changes as the author types.
 *
 * **Every safety rule of the single-file hook is kept, for the same reasons:**
 *
 *   * The bytes come through the authenticated client — an `<img src>` pointing at the API
 *     would send no bearer token and 401, which is the whole reason the stored markup carries
 *     no URL.
 *   * The kind is decided by the SERVER's verified media type through `previewKindFor`, never
 *     by the attachment's name or extension. Anything that is not an image resolves to nothing.
 *   * The blob is RE-TYPED on construction, so a byte stream the server verified as a PNG is
 *     handed to the browser as a PNG and not as whatever a response header claimed.
 *   * Every URL is revoked when it is replaced or the component goes, so a long editing session
 *     does not leak one per keystroke.
 *
 * An id that resolves to nothing is simply absent from the map, and both call sites draw a
 * placeholder rather than a broken picture.
 */
export function useAttachmentImages(
  ids: readonly string[],
  load: (id: string) => Promise<{ blob: Blob; contentType: string | null }>,
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});

  /*
    The resolved set, kept in a ref so the effect can revoke exactly what it created without
    the map becoming a dependency of the effect that writes it.
  */
  const resolved = useRef<Record<string, string>>({});

  // A stable key, so re-ordering or re-rendering does not refetch what is already held.
  const wanted = [...new Set(ids)].sort().join(',');

  useEffect(() => {
    let cancelled = false;
    const list = wanted ? wanted.split(',') : [];

    // Drop what is no longer referenced before fetching, so deleting a picture frees it now.
    for (const [id, url] of Object.entries(resolved.current)) {
      if (!list.includes(id)) {
        URL.revokeObjectURL(url);
        delete resolved.current[id];
      }
    }

    const missing = list.filter((id) => !resolved.current[id]);
    if (missing.length === 0) {
      setUrls({ ...resolved.current });
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      for (const id of missing) {
        try {
          const { blob, contentType } = await load(id);
          if (cancelled) return;

          // The server's verified type decides, and only an image is ever drawn.
          if (previewKindFor(contentType) !== 'image') continue;

          resolved.current[id] = URL.createObjectURL(new Blob([blob], { type: contentType! }));
        } catch {
          // A picture that cannot be fetched is one the document draws a placeholder for.
          // It is not an error the form should report: the text is still readable.
        }
      }

      if (!cancelled) setUrls({ ...resolved.current });
    })();

    return () => {
      cancelled = true;
    };
  }, [wanted, load]);

  // Revoke everything on unmount — the ref holds exactly what this hook created.
  useEffect(
    () => () => {
      for (const url of Object.values(resolved.current)) URL.revokeObjectURL(url);
      resolved.current = {};
    },
    [],
  );

  return urls;
}

/** The attachment ids an `<img data-attachment>` document references, in document order. */
export function attachmentIdsIn(html: string | null | undefined): string[] {
  if (!html) return [];

  const ids: string[] = [];
  // Parsed, never regexed: the value is read off a real attribute node.
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('img[data-attachment]').forEach((img) => {
    const id = img.getAttribute('data-attachment');
    /*
      A GUID or nothing. The renderer is what turns an id into a request, so this is the point
      where a junk value stops — the sanitizer deliberately does not check the shape, because
      checking it HERE is what makes a bad value draw no picture instead of naming a URL.
    */
    if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      ids.push(id);
    }
  });

  return ids;
}
